/**
 * Блок сводки движений платёжных систем (карточка BL-2026-09-29-beba).
 *
 * Flow: опрос /api/treasury/psp-flow-stats раз в 60 секунд → таблица
 *       «система × направление» на три календарных периода: сутки, неделя,
 *       месяц.
 *
 * Зависимости:
 *   - react: состояние и жизненный цикл;
 *   - api.js: loadPspFlowStats.
 *
 * Связанные файлы:
 *   - src/App.jsx — размещает блок в разделе PSP/Bank;
 *   - src/PspTransactionsPanel.jsx — сами движения строками.
 *
 * # ГРАНИЦА ПЕРИОДА НАЗЫВАЕТСЯ, А НЕ ПОДРАЗУМЕВАЕТСЯ
 *
 * Периоды календарные: сутки — с полуночи UTC, неделя — с понедельника,
 * месяц — с первого числа. «Неделя» во вторник означает двое суток, и это
 * обязано быть видно: рядом с заголовком стоит дата начала. Без неё число
 * «14 за неделю» нельзя ни проверить, ни сверить с выпиской — читающий
 * будет гадать, скользящее это окно или календарное.
 */

import { useCallback, useEffect, useState } from 'react'

import { loadPspFlowStats } from './api'

/** Период опроса — как у остальных блоков платёжного контура. */
const POLL_MS = 60000

/** Названия платёжных систем. */
const SOURCE_TITLES = {
  praxis: 'Praxis Cashier',
  b2binpay: 'B2BinPay',
  coinsbuy: 'Coinsbuy',
}

/** Периоды в порядке показа: от узкого к широкому. */
export const PERIODS = [
  { code: 'day', title: 'Сутки' },
  { code: 'week', title: 'Неделя' },
  { code: 'month', title: 'Месяц' },
]

/**
 * Показывает границу периода днём и месяцем.
 *
 * @param {string|undefined} iso — момент начала периода
 * @returns {string} «с 28.09» либо пустая строка, если границы нет
 */
export function formatBound(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const дд = String(d.getUTCDate()).padStart(2, '0')
  const мм = String(d.getUTCMonth() + 1).padStart(2, '0')
  return `с ${дд}.${мм}`
}

/**
 * Собирает счётчики в таблицу «система → период → направление».
 *
 * @param {Array} counts — плоский перечень счётчиков с сервера
 * @param {Array<string>} sources — включённые системы
 * @returns {Array} строки по системам, у каждой — счётчики по периодам
 *
 * Система без единого движения остаётся строкой с нулями, а не исчезает:
 * пропавшая строка читается как «такой системы нет», а не как «оборота
 * не было» — а это разные вещи.
 */
export function buildRows(counts, sources) {
  const пусто = () => ({ deposit: null, withdrawal: null })
  const rows = new Map()
  for (const s of sources || []) {
    const periods = {}
    for (const p of PERIODS) periods[p.code] = пусто()
    rows.set(s, { source: s, periods })
  }
  for (const c of counts || []) {
    if (!rows.has(c.source_code)) {
      const periods = {}
      for (const p of PERIODS) periods[p.code] = пусто()
      rows.set(c.source_code, { source: c.source_code, periods })
    }
    const p = rows.get(c.source_code).periods[c.period]
    if (!p) continue
    p[c.direction] = c
  }
  return [...rows.values()]
}

/**
 * Показывает одно направление внутри клетки: подпись и число.
 *
 * @param {string} label — «ввод» либо «вывод»
 * @param {object|null} c — счётчик либо null, если движений не было
 *
 * Ноль показывается ЦИФРОЙ, а не прочерком: здесь ноль — измеренная
 * величина («за сутки не прошло ни одного»), а не отсутствие измерения.
 */
function Направление({ label, c }) {
  return (
    <div className="flow-line">
      <span className="muted">{label}</span>
      <span className="flow-num">{c ? c.completed : 0}</span>
      {/* Незавершённое и отклонённое названы отдельно: слитые с проведённым,
          они выдали бы намерение за состоявшийся платёж (BR-023). */}
      {c && c.pending > 0 && (
        <span className="warn cell-note" title="движения ещё не завершились">
          +{c.pending} в работе
        </span>
      )}
      {c && c.rejected > 0 && (
        <span className="muted cell-note" title="отказы и отмены">
          {c.rejected} откл.
        </span>
      )}
    </div>
  )
}

export default function PspFlowStatsPanel() {
  const [report, setReport] = useState(null)
  const [error, setError] = useState('')

  const load = useCallback(() => {
    loadPspFlowStats()
      .then((d) => {
        setReport(d)
        setError('')
      })
      .catch((e) => setError(e.message))
  }, [])

  useEffect(() => {
    load()
    const timer = setInterval(load, POLL_MS)
    return () => clearInterval(timer)
  }, [load])

  if (error) {
    return (
      <section className="card">
        <h2>Транзакции PSP</h2>
        <div className="error">{error}</div>
      </section>
    )
  }

  if (!report) {
    return (
      <section className="card">
        <h2>Транзакции PSP</h2>
        <p className="muted">Загрузка…</p>
      </section>
    )
  }

  if (!report.sources || report.sources.length === 0) return null

  const bounds = {}
  for (const b of report.bounds || []) bounds[b.period] = b.from
  const rows = buildRows(report.counts, report.sources)

  return (
    <section className="card">
      <h2>Транзакции PSP</h2>
      <p className="muted metric-note">
        Календарные периоды в UTC: сутки с полуночи, неделя с понедельника,
        месяц с первого числа.
      </p>

      {/* Строка — ПЕРИОД, столбец — система, ввод и вывод внутри клетки.
          Прежде было наоборот: строка — система, а каждый период занимал
          два столбца, и таблица из семи столбцов требовала 450 пикселей.
          В колонке сводных показателей (около 380) она обрезалась. Разрез
          тот же, ни одно число не потеряно — сменилась только развёртка. */}
      <table>
        <thead>
          <tr>
            <th>Период</th>
            {rows.map((r) => (
              <th key={r.source}>{SOURCE_TITLES[r.source] || r.source}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {PERIODS.map((p) => (
            <tr key={p.code}>
              <td>
                {p.title}
                <div className="muted cell-note">{formatBound(bounds[p.code])}</div>
              </td>
              {rows.map((r) => (
                <td key={r.source}>
                  <Направление label="ввод" c={r.periods[p.code].deposit} />
                  <Направление label="вывод" c={r.periods[p.code].withdrawal} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}
